import {createSlice, isAnyOf} from '@reduxjs/toolkit';
import {FLEX_RATES_CONFIG_DURATION_IN_MIN} from '../utils/utils';

import {
    addAndValidateNewRateBand,
    deleteRateBand,
    deleteRateSegment,
    getFlexRatesData,
    getFlexRatesMatrixData,
    getFlexRatesRatingRuleGroups,
    previewFlexRatesMatrixData,
    saveFlexRatesData,
    setPreviewState,
    updateAndValidateRateBands,
    updateAndValidateRateSegments,
    updateInterpolation,
    resetRateMatrices,
    getHoursOfOperation,
    getMultiDayDiscountData,
    updateAndValidateMultiDayDiscounts,
    deleteMultiDayDiscount,
    saveMultiDayDiscounts,
    addAndValidateNewMultiDayDiscount,
} from './flex-rates-actions';

import {
    multiDayDiscountsEntityAdapter,
    rateBandsEntityAdapter,
    rateSegmentsEntityAdapter,
} from './flex-rates-adapters';

/**
 * @constant {Object} FlexRatesState
 * @type {{isLoading: boolean, lastUpdated: string, durationInMin: number<FLEX_RATES_CONFIG_DURATION_IN_MIN>,  rateSegments: import("@reduxjs/toolkit").EntityState<TFlexRateFormattedRateSegment>, isPreview: boolean, rateMatricies: {}, rateBands: import("@reduxjs/toolkit").EntityState<TFlexRateFormattedRateBand>, id: null, hasUpdated: boolean, ruleGroupId: number, error: null, isSaving: boolean, ruleGroups: []}}
 */
const initialState = {
    rateBands: rateBandsEntityAdapter.getInitialState(),
    rateSegments: rateSegmentsEntityAdapter.getInitialState(),
    rateMatricies: {},
    ruleGroups: [],
    durationInMin: FLEX_RATES_CONFIG_DURATION_IN_MIN['30min'],
    id: null,
    lastUpdated: '',
    isLoading: false,
    isPreview: false,
    isSaving: false,
    hasUpdated: false,
    ruleGroupId: null,
    error: null,
    functionalHoursOfOperation: [],
    multiDayDiscounts: {
        isLoading: false,
        lastModified: '',
        discounts: multiDayDiscountsEntityAdapter.getInitialState(),
    },
};

const flexRatesSlice = createSlice({
    name: 'flexRates',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(getHoursOfOperation.fulfilled, (state, {payload}) => {
                state.functionalHoursOfOperation = payload;
            })
            .addCase(resetRateMatrices, (state) => {
                state.rateMatricies = {};
                state.id = null;
            })
            .addCase(
                addAndValidateNewRateBand.fulfilled,
                (state, {payload}) => {
                    rateBandsEntityAdapter.setAll(state.rateBands, payload);
                    state.hasUpdated = true;
                }
            )
            .addCase(
                updateAndValidateRateBands.fulfilled,
                (state, {payload}) => {
                    rateBandsEntityAdapter.setAll(state.rateBands, payload);
                    state.hasUpdated = true;
                }
            )
            .addCase(
                updateAndValidateRateSegments.fulfilled,
                (state, {payload}) => {
                    rateSegmentsEntityAdapter.setAll(
                        state.rateSegments,
                        payload
                    );
                    state.hasUpdated = true;
                }
            )
            .addCase(deleteRateBand, (state, {payload}) => {
                rateBandsEntityAdapter.removeOne(state.rateBands, payload);
            })
            .addCase(deleteRateSegment, (state, {payload}) => {
                rateBandsEntityAdapter.removeOne(state.rateSegments, payload);
            })
            .addCase(getFlexRatesMatrixData.fulfilled, (state, {payload}) => {
                state.rateMatricies = payload;
                state.isLoading = false;
            })
            .addCase(getMultiDayDiscountData.pending, (state) => {
                state.multiDayDiscounts.isLoading = true;
            })
            .addCase(getMultiDayDiscountData.fulfilled, (state, {payload}) => {
                const {discounts, lastModified} = payload;

                multiDayDiscountsEntityAdapter.setAll(
                    state.multiDayDiscounts.discounts,
                    discounts
                );
                state.multiDayDiscounts.lastModified = lastModified;
                state.multiDayDiscounts.isLoading = false;
            })
            .addCase(getMultiDayDiscountData.rejected, (state, {payload}) => {
                state.multiDayDiscounts.isLoading = false;
                state.error = payload;
            })
            .addCase(
                updateAndValidateMultiDayDiscounts.fulfilled,
                (state, {payload}) => {
                    multiDayDiscountsEntityAdapter.upsertOne(
                        state.multiDayDiscounts.discounts,
                        payload
                    );

                    state.hasUpdated = true;
                }
            )
            .addCase(
                addAndValidateNewMultiDayDiscount.fulfilled,
                (state, {payload}) => {
                    multiDayDiscountsEntityAdapter.upsertOne(
                        state.multiDayDiscounts.discounts,
                        payload
                    );
                    state.hasUpdated = true;
                }
            )
            .addCase(deleteMultiDayDiscount, (state, {payload}) => {
                multiDayDiscountsEntityAdapter.removeOne(
                    state.multiDayDiscounts.discounts,
                    payload
                );

                state.hasUpdated = true;
            })
            .addCase(saveMultiDayDiscounts.fulfilled, (state, {payload}) => {
                const {discounts, lastModified} = payload;

                multiDayDiscountsEntityAdapter.setAll(
                    state.multiDayDiscounts.discounts,
                    discounts
                );
                state.multiDayDiscounts.lastModified = lastModified;
                state.isSaving = false;
                state.hasUpdated = false;
            })
            .addCase(setPreviewState, (state, {payload}) => {
                state.isPreview = payload;
            })
            .addCase(updateInterpolation, (state, {payload}) => {
                state.interpolation = payload;
                state.hasUpdated = true;
            })
            .addCase(
                getFlexRatesRatingRuleGroups.fulfilled,
                (state, {payload}) => {
                    state.ruleGroups = payload || [];
                    state.error = null;
                }
            )
            .addMatcher(
                isAnyOf(
                    saveFlexRatesData.rejected,
                    previewFlexRatesMatrixData.rejected,
                    saveMultiDayDiscounts.rejected
                ),
                (state) => {
                    state.isLoading = false;
                    state.isPreview = false;
                    state.isSaving = false;
                    state.hasUpdated = false;
                }
            )
            .addMatcher(
                isAnyOf(
                    getFlexRatesData.fulfilled,
                    saveFlexRatesData.fulfilled
                ),
                (state, {payload}) => {
                    const {
                        rateBands,
                        rateSegments,
                        rateTriggeredSegments,
                        interpolation,
                        durationInMin,
                        ruleGroupId,
                    } = payload.config;

                    const combinedSegments = [
                        ...rateSegments,
                        ...rateTriggeredSegments,
                    ];

                    rateBandsEntityAdapter.setAll(state.rateBands, rateBands);
                    rateSegmentsEntityAdapter.setAll(
                        state.rateSegments,
                        combinedSegments
                    );

                    state.id = payload.id;
                    state.interpolation = interpolation;
                    state.durationInMin = durationInMin;
                    state.ruleGroupId = ruleGroupId;
                    state.lastUpdated = payload.created;
                    state.isLoading = false;
                    state.isSaving = false;
                    state.hasUpdated = false;
                    state.error = null;
                }
            )
            .addMatcher(
                isAnyOf(
                    getFlexRatesMatrixData.fulfilled,
                    previewFlexRatesMatrixData.fulfilled
                ),
                (state, {payload}) => {
                    state.rateMatricies = payload;
                    state.isLoading = false;
                    state.isSaving = false;
                    state.hasUpdated = false;
                }
            )
            .addMatcher(
                isAnyOf(
                    saveFlexRatesData.pending,
                    saveMultiDayDiscounts.pending
                ),
                (state) => {
                    state.isSaving = true;
                }
            )
            .addMatcher(
                isAnyOf(
                    previewFlexRatesMatrixData.pending,
                    getFlexRatesData.pending
                ),
                (state) => {
                    state.isLoading = true;
                }
            )
            .addMatcher(
                isAnyOf(
                    getFlexRatesMatrixData.rejected,
                    getFlexRatesData.rejected,
                    getFlexRatesRatingRuleGroups.rejected
                ),
                (state, {payload}) => {
                    state.error = payload;
                    state.isLoading = false;
                }
            );
    },
});

export default flexRatesSlice.reducer;
